import { useState } from 'react';

import { SMU_TO_GBM_PASSWORD_REQUIRED_CODE } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { Controller, useForm } from 'react-hook-form';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { env } from '@/env';
import { awaCurrentSitemateUserKeys } from '@/queries/awa';
import { AWAApiV2Service } from '@/services/awa-api-v2';
import { useAWAStore } from '@/stores/awa.store';

import { AWAOnboardingCarousel } from './onboarding-carousel';
import { hasSeenAwaOnboarding, markAwaOnboardingSeen } from './onboarding-seen';

const emailSchema = z.object({
  email: z.string().email('Enter a valid email address'),
});
type EmailForm = z.infer<typeof emailSchema>;

const otpSchema = z.object({
  code: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code'),
});
type OtpForm = z.infer<typeof otpSchema>;

const passwordSchema = z
  .object({
    password: z
      .string()
      .min(10, 'Password must be at least 10 characters')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/\d/, 'Password must contain at least one number'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });
type PasswordForm = z.infer<typeof passwordSchema>;

type Step = 'intro' | 'email' | 'otp' | 'password';

async function parseErrorBody(error: unknown): Promise<{ message?: string; code?: string }> {
  if (error instanceof HTTPError) {
    const body = await error.response.json<{ message?: string; code?: string }>().catch(() => undefined);
    if (body) {
      return body;
    }
  }
  return {};
}

export function AWAAuthEmailSetupPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { authTokens } = useAWAStore();

  const [step, setStep] = useState<Step>(() => (hasSeenAwaOnboarding() ? 'email' : 'intro'));
  const [email, setEmail] = useState('');
  const [completedAction, setCompletedAction] = useState<'created' | 'linked' | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const emailForm = useForm<EmailForm>({ resolver: zodResolver(emailSchema), defaultValues: { email: '' }, mode: 'onChange' });
  const otpForm = useForm<OtpForm>({ resolver: zodResolver(otpSchema), defaultValues: { code: '' }, mode: 'onChange' });
  const passwordForm = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { password: '', confirmPassword: '' },
    mode: 'onChange',
  });

  const initiate = useMutation({
    mutationFn: (data: EmailForm) => AWAApiV2Service.InitiateAwaMigration(data),
    onSuccess: (_result, variables) => {
      setEmail(variables.email.toLowerCase());
      setStep('otp');
    },
    onError: async (error) => {
      const { message } = await parseErrorBody(error);
      setErrorMessage(message ?? 'Something went wrong. Please try again.');
    },
  });

  const finish = useMutation({
    mutationFn: async (password: string | undefined) => (await AWAApiV2Service.FinishAwaMigration({ email, password })).json(),
    onSuccess: async (result) => {
      // CREATE keeps the mechanic's identity with a new email — refresh the cached
      // account so the route guard stops redirecting here. LINK retires this
      // identity, so the mechanic signs in fresh with the existing account instead.
      if (result.action === 'created') {
        await queryClient.invalidateQueries({ queryKey: awaCurrentSitemateUserKeys.all });
      }
      setCompletedAction(result.action);
    },
    onError: async (error) => {
      const { message, code } = await parseErrorBody(error);
      // The no-password LINK attempt hit the migration's CREATE branch (the email
      // has no FusionAuth account) — the flow continues on the set-password step.
      if (code === SMU_TO_GBM_PASSWORD_REQUIRED_CODE) {
        setStep('password');
        return;
      }
      setErrorMessage(message ?? 'Something went wrong. Please try again.');
    },
  });

  const verify = useMutation({
    mutationFn: async (data: OtpForm) => (await AWAApiV2Service.VerifyAwaMigrationOtp({ email, code: data.code })).json(),
    onSuccess: (result) => {
      if (result.requiresPassword) {
        setStep('password');
      } else {
        // The email already belongs to a Gearbelt account — link to it, no new
        // password. mutate (not mutateAsync): failures are handled by finish's
        // own onError, including the password-required fallback.
        finish.mutate(undefined);
      }
    },
    onError: async (error) => {
      const { message } = await parseErrorBody(error);
      setErrorMessage(message ?? 'Something went wrong. Please try again.');
    },
  });

  // A migrated mechanic signs in fresh on GBMW (Gearbelt Mobile Web) — with their
  // new email + password after CREATE, or the existing account after LINK. Falls
  // back to AWA login if the host isn't configured.
  function goToGbmwSignIn() {
    if (env.VITE_GBMW_URL) {
      window.location.href = env.VITE_GBMW_URL;
    } else {
      navigate('/auth/login');
    }
  }

  // This page lives outside AWARoute (so the guard's migrate redirect can't loop),
  // which means it must enforce auth itself. The preserved query string lets the
  // login flow route back through the guard, which re-derives the redirect here.
  if (!authTokens?.accessToken) {
    return <Navigate to={`/auth/login?${searchParams.toString()}`} replace />;
  }

  if (step === 'intro') {
    return (
      <AWAOnboardingCarousel
        onComplete={() => {
          markAwaOnboardingSeen();
          setStep('email');
        }}
      />
    );
  }

  if (completedAction === 'linked') {
    return (
      <div className="text-default-text flex h-screen flex-col gap-4 px-6 py-25">
        <h1 className="text-2xl font-bold">Account linked</h1>
        <p>
          <span className="font-medium">{email}</span> already has a Gearbelt account, so we&apos;ve linked you to it. Sign in with that account&apos;s email
          and password to continue.
        </p>
        <Button className="text-base" type="button" onClick={goToGbmwSignIn}>
          Go to sign in
        </Button>
      </div>
    );
  }

  if (completedAction === 'created') {
    return (
      <div className="text-default-text flex h-screen flex-col gap-4 px-6 py-25">
        <h1 className="text-2xl font-bold">You&apos;re a Gearbelt member</h1>
        <p>
          Your account is set up with <span className="font-medium">{email}</span>. You can sign in to Gearbelt with your email and password.
        </p>
        <Button className="text-base" type="button" onClick={goToGbmwSignIn}>
          Continue
        </Button>
      </div>
    );
  }

  // Each step is a separate react-hook-form instance rendered at the same tree
  // position; without a per-step key React reuses the <input> DOM node and the
  // new form's controlled value never updates after a step transition.
  if (step === 'otp') {
    return (
      <form
        key={step}
        onSubmit={otpForm.handleSubmit((data) => {
          setErrorMessage(null);
          verify.mutate(data);
        })}
        className="text-default-text flex h-screen flex-col gap-4 px-6 py-25">
        <h1 className="text-2xl font-bold">Enter your code</h1>
        <p>
          We&apos;ve sent a 6-digit code to <span className="font-medium">{email}</span>. Enter it below to verify your email.
        </p>

        <Controller
          control={otpForm.control}
          name="code"
          render={({ field }) => (
            <Label className="flex flex-col gap-1 text-base">
              <span>Verification code</span>
              <Input
                type="text"
                inputMode="numeric"
                maxLength={6}
                autoComplete="one-time-code"
                value={field.value}
                placeholder="123456"
                className="text-base"
                error={!!otpForm.formState.errors.code}
                onChange={field.onChange}
              />
              {otpForm.formState.errors.code && <span className="text-default-red text-sm">{otpForm.formState.errors.code.message}</span>}
            </Label>
          )}
        />

        {errorMessage && <span className="text-default-red text-sm">{errorMessage}</span>}

        <Button className="text-base" disabled={!otpForm.formState.isValid} loading={verify.isPending || finish.isPending}>
          Verify
        </Button>

        <button
          type="button"
          className="text-default-blue text-sm underline"
          onClick={() => {
            setErrorMessage(null);
            initiate.mutate({ email });
          }}>
          Resend code
        </button>
      </form>
    );
  }

  if (step === 'password') {
    return (
      <form
        key={step}
        onSubmit={passwordForm.handleSubmit((data) => {
          setErrorMessage(null);
          finish.mutate(data.password);
        })}
        className="text-default-text flex h-screen flex-col gap-4 px-6 py-25">
        <h1 className="text-2xl font-bold">Set a password</h1>
        <p>Create a password to finish setting up your Gearbelt account.</p>

        <Controller
          control={passwordForm.control}
          name="password"
          render={({ field }) => (
            <Label className="flex flex-col gap-1 text-base">
              <span>Password</span>
              <Input
                type="password"
                autoComplete="new-password"
                value={field.value}
                placeholder="Enter a password..."
                className="text-base"
                error={!!passwordForm.formState.errors.password}
                onChange={field.onChange}
              />
              {passwordForm.formState.errors.password && <span className="text-default-red text-sm">{passwordForm.formState.errors.password.message}</span>}
            </Label>
          )}
        />

        <Controller
          control={passwordForm.control}
          name="confirmPassword"
          render={({ field }) => (
            <Label className="flex flex-col gap-1 text-base">
              <span>Confirm password</span>
              <Input
                type="password"
                autoComplete="new-password"
                value={field.value}
                placeholder="Re-enter your password..."
                className="text-base"
                error={!!passwordForm.formState.errors.confirmPassword}
                onChange={field.onChange}
              />
              {passwordForm.formState.errors.confirmPassword && (
                <span className="text-default-red text-sm">{passwordForm.formState.errors.confirmPassword.message}</span>
              )}
            </Label>
          )}
        />

        {errorMessage && <span className="text-default-red text-sm">{errorMessage}</span>}

        <Button className="text-base" disabled={!passwordForm.formState.isValid} loading={finish.isPending}>
          Finish
        </Button>
      </form>
    );
  }

  return (
    <form
      key={step}
      onSubmit={emailForm.handleSubmit((data) => {
        setErrorMessage(null);
        initiate.mutate(data);
      })}
      className="text-default-text flex h-screen flex-col gap-4 px-6 py-25">
      <h1 className="text-2xl font-bold">Set up your Gearbelt account</h1>

      <p>Add your email to become a Gearbelt member. We&apos;ll send a 6-digit code to verify it.</p>

      <Controller
        control={emailForm.control}
        name="email"
        render={({ field }) => (
          <Label className="flex flex-col gap-1 text-base">
            <span>Email</span>
            <Input
              type="email"
              value={field.value}
              placeholder="Enter your email..."
              className="text-base"
              error={!!emailForm.formState.errors.email}
              onChange={field.onChange}
            />
            {emailForm.formState.errors.email && <span className="text-default-red text-sm">{emailForm.formState.errors.email.message}</span>}
          </Label>
        )}
      />

      {errorMessage && <span className="text-default-red text-sm">{errorMessage}</span>}

      <Button className="text-base" disabled={!emailForm.formState.isValid} loading={initiate.isPending}>
        Continue
      </Button>
    </form>
  );
}
